import {isBefore, startOfDay} from 'date-fns';
import {Card, Form, FormButton, FormField, FormSubmitHandler} from 'platform/components';
import {Grid, HStack, VStack} from 'platform/foundation';
import {object, SchemaOf, string} from 'yup';

import {useUpdateOfferAlternativeMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import {handleOfferAlternativeError} from './handleOfferAlternativeError';
import {type OfferAlternative} from './OfferAlternative';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

type OfferTermsFormValues = {
  name: string | null;
  validUntil: string | null;
  deliveryNote: string | null;
  customerNote: string | null;
};

interface AlternativeOfferTermsFormProps {
  businessCaseId: string;
  alternative: OfferAlternative;
}

/** Customer-facing name, validity and notes of a non-applied alternative. */
export function AlternativeOfferTermsForm(props: AlternativeOfferTermsFormProps) {
  const [patchAlternative, {isLoading}] = useUpdateOfferAlternativeMutation();
  const {canEdit, terms} = props.alternative;

  const defaultValues: OfferTermsFormValues = {
    name: props.alternative.name,
    validUntil: terms.validUntil,
    deliveryNote: terms.deliveryNote,
    customerNote: terms.customerNote,
  };

  const handleSubmit: FormSubmitHandler<OfferTermsFormValues> = (
    values,
    _setErrors,
    reset,
    blockerContext
  ) =>
    patchAlternative({
      businessCaseId: props.businessCaseId,
      alternativeId: props.alternative.id,
      updateOfferAlternativeRequestBody: {
        expectedRevision: props.alternative.revision,
        name: values.name || null,
        validUntil: values.validUntil || null,
        deliveryNote: values.deliveryNote || null,
        customerNote: values.customerNote || null,
      },
    })
      .unwrap()
      .then(() => reset(values))
      .catch((error: unknown) =>
        handleOfferAlternativeError(error, {signalApiError: blockerContext?.reportApiFailure})
      );

  return (
    <Form<OfferTermsFormValues>
      // Remount only when this form's own server values change, not on a sibling save.
      key={JSON.stringify(defaultValues)}
      onSubmit={handleSubmit}
      schema={getSchema(terms.validUntil)}
      shouldWatchForUnsavedChanges
      defaultValues={defaultValues}
    >
      {(control, formApi) => (
        <Card
          variant="inlineGrey"
          title={t('entity.offerAlternative.terms.title', 'Offer terms for the customer')}
          data-testid="offerAlternativeTerms"
        >
          <VStack spacing={4}>
            <Grid columns={[1, 2]}>
              <FormField
                type="text"
                name="name"
                control={control}
                label={t('entity.offerAlternative.terms.name', 'Name shown to the customer')}
                isDisabled={!canEdit}
              />
              <FormField
                type="apiDate"
                name="validUntil"
                control={control}
                label={t('entity.offerAlternative.terms.validUntil', 'Valid until')}
                minDate={startOfDay(new Date())}
                isDisabled={!canEdit}
              />
              <FormField
                type="textarea"
                name="deliveryNote"
                control={control}
                label={t(
                  'entity.offerAlternative.terms.deliveryNote',
                  'Delivery information for the customer'
                )}
                isDisabled={!canEdit}
              />
              <FormField
                type="textarea"
                name="customerNote"
                control={control}
                label={t('entity.offerAlternative.terms.customerNote', 'Customer-visible note')}
                isDisabled={!canEdit}
              />
            </Grid>
            <HStack justify="flex-end">
              <FormButton
                type="submit"
                control={control}
                title={i18n.t('general.actions.save')}
                isDisabled={!canEdit || !formApi.formState.isDirty}
                isLoading={isLoading}
              />
            </HStack>
          </VStack>
        </Card>
      )}
    </Form>
  );
}

// A new validity cannot lie in the past; an already saved one may stay while other terms change.
const getSchema = (savedValidUntil: string | null): SchemaOf<OfferTermsFormValues> =>
  object().shape({
    name: string().defined().nullable(),
    validUntil: string()
      .defined()
      .nullable()
      .test(
        'notInPast',
        t(
          'entity.offerAlternative.terms.validUntilPast',
          'The offer validity cannot be in the past.'
        ),
        (value) =>
          !value || value === savedValidUntil || !isBefore(parseDate(value), startOfDay(new Date()))
      ),
    deliveryNote: string().defined().nullable(),
    customerNote: string().defined().nullable(),
  });
